/**
 * Deciding whether a phone number from the API is safe to put behind a `tel:`.
 *
 * This exists because of a real data hazard, not as defensive decoration. Every
 * partner hospital's `hospitals.phone` is still the literal seed placeholder
 * `'+233-XX-XXX-XXXX'` (M2-T01 seed; owner-confirms the real numbers). Rendering
 * that as a link gives a patient a Call button that cannot dial — in a health
 * context a dead call action is worse than no call action, because it costs the
 * patient the attempt before they look for another route.
 *
 * So callers ask `dialablePhone(raw)` and render the button only on a non-null
 * result. When the real numbers land the buttons appear on their own — no code
 * change, no follow-up ticket.
 */

/** Ghana numbers are 9 significant digits after the +233 country code; local
 * form is 10 digits starting 0. Anything shorter is not a real line. */
const MIN_DIGITS = 9
const MAX_DIGITS = 15 // E.164 ceiling

/**
 * Normalise a raw phone value to a `tel:`-safe string, or null when it is not
 * dialable (absent, placeholder, or too short to be a real number).
 *
 * Returns digits with an optional leading `+`, separators stripped — dialers
 * accept spaces and dashes, but stripping keeps the rendered href predictable
 * and testable.
 */
export function dialablePhone(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null

  // Any non-separator, non-digit character means a placeholder or free text
  // ('+233-XX-XXX-XXXX', 'ask reception'). Reject rather than strip it out:
  // stripping 'XX' would silently yield a short but plausible-looking number.
  if (!/^\+?[\d\s().-]+$/.test(trimmed)) return null

  const plus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length < MIN_DIGITS || digits.length > MAX_DIGITS) return null

  return plus ? `+${digits}` : digits
}
